import { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { aiGenerateStyles, aiApplyOptions } from "../lib/api";
import { compressImage } from "../lib/image";
import QuickLeadPanel from "./QuickLeadPanel";
import { UploadArea } from "./configurator/UploadArea";
import { PhotoIntro } from "./configurator/PhotoIntro";
import { ResultPreview } from "./configurator/ResultPreview";
import { VariantsGrid } from "./configurator/VariantsGrid";
import { OptionsPanel } from "./configurator/OptionsPanel";

export default function Configurator({ onRequestQuote }) {
  const [photo, setPhoto] = useState(null);
  const [aiVariants, setAiVariants] = useState([]); // [{style,data_url}]
  const [selectedVariant, setSelectedVariant] = useState(null); // style name
  const [enduit, setEnduit] = useState(null);
  const [deco, setDeco] = useState([]);
  const [light, setLight] = useState([]);
  const [generated, setGenerated] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const fileRef = useRef(null);

  const onPick = useCallback(async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Photo trop volumineuse (max 20 Mo)");
      return;
    }
    try {
      const compressed = await compressImage(file, { maxDim: 1600, quality: 0.82 });
      setPhoto(compressed);
      setAiVariants([]);
      setSelectedVariant(null);
      setEnduit(null);
      setDeco([]);
      setLight([]);
      setGenerated(null);
    } catch {
      toast.error("Impossible de lire cette photo. Réessayez avec un autre fichier.");
    }
  }, []);

  const onFileChange = (e) => onPick(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    onPick(e.dataTransfer.files?.[0]);
  };
  const openPicker = () => fileRef.current?.click();

  const toggle = (list, setList, item) =>
    setList(list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);

  const fallbackToQuote = (reason = "") => {
    toast(
      "L'IA est momentanément surchargée — recevez quand même votre devis gratuit.",
      {
        description: reason || "Votre photo et vos options seront jointes à la demande.",
        action: {
          label: "Demander mon devis",
          onClick: () => requestQuote(),
        },
        duration: 12000,
      }
    );
  };

  const generateAI = async () => {
    if (!photo) return;
    setAiLoading(true);
    toast.message("Génération IA en cours…", {
      description: "5 propositions personnalisées (≈45 secondes)",
    });
    try {
      const base64 = photo.split(",")[1] ?? photo;
      const res = await aiGenerateStyles(base64);
      const valid = (res.images || []).filter((i) => i.data_url);
      if (valid.length === 0) {
        fallbackToQuote("Aucune proposition n'a pu être générée.");
      } else {
        setAiVariants(valid);
        setSelectedVariant(valid[0].style);
        toast.success(`${valid.length} propositions générées`);
        setTimeout(() => {
          document
            .getElementById("ai-variants-grid")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    } catch {
      fallbackToQuote();
    } finally {
      setAiLoading(false);
    }
  };

  const hasAnyOption = enduit || deco.length > 0 || light.length > 0;
  const totalSelected = (enduit ? 1 : 0) + deco.length + light.length;

  const regenerate = async () => {
    const baseImage = generated
      || aiVariants.find((v) => v.style === selectedVariant)?.data_url;
    if (!baseImage) {
      toast.error("Sélectionnez d'abord une proposition IA");
      return;
    }
    if (!hasAnyOption) {
      toast.error("Sélectionnez au moins une option avant de régénérer");
      return;
    }
    setApplyLoading(true);
    toast.message("Application des options…", {
      description: "L'IA régénère votre façade (≈30 secondes)",
    });
    try {
      const base64 = baseImage.split(",")[1] ?? baseImage;
      const res = await aiApplyOptions({
        image_base64: base64,
        enduit_color: enduit?.label || "",
        decorative_options: deco,
        lighting_options: light,
        renovation_options: [],
        custom_options: [],
      });
      if (!res?.data_url) {
        fallbackToQuote("Le rendu n'a pas pu être généré.");
      } else {
        setGenerated(res.data_url);
        toast.success("Votre façade a été régénérée");
        setTimeout(() => {
          document
            .getElementById("configurator-result")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    } catch {
      fallbackToQuote();
    } finally {
      setApplyLoading(false);
    }
  };

  const newModification = () => {
    setGenerated(null);
    setTimeout(() => {
      document
        .getElementById("configurator-options")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const variantImage = aiVariants.find((v) => v.style === selectedVariant)?.data_url;
  const displayedImage = generated || variantImage || photo;

  const requestQuote = () => {
    onRequestQuote?.({
      enduit_color: enduit?.label || "",
      decorative_options: deco,
      lighting_options: light,
      renovation_options: [],
      custom_options: selectedVariant ? [`Base IA : ${selectedVariant}`] : [],
      facade_photo_base64: photo || "",
      configured_photo_base64: generated || variantImage || "",
    });
  };

  const buildQuickLeadPayload = () => ({
    message:
      "Demande envoyée depuis le Configurateur IA façade — rappel rapide souhaité.",
    enduit_color: enduit?.label || "",
    decorative_options: deco,
    lighting_options: light,
    renovation_options: [],
    custom_options: selectedVariant ? [`Base IA : ${selectedVariant}`] : [],
    facade_photo_base64: photo || "",
    configured_photo_base64: generated || variantImage || "",
  });

  const selectVariant = (style) => {
    setSelectedVariant(style);
    setGenerated(null);
  };

  return (
    <section
      id="configurateur"
      data-testid="configurator-section"
      className="relative py-24 md:py-32 bg-gradient-to-b from-[#050505] via-[#0a0a0a] to-[#050505]"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="mb-12 text-center max-w-3xl mx-auto"
        >
          <div className="eyebrow mb-4 inline-flex items-center gap-2">
            <Sparkles size={12} />
            <span>Configurateur IA</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-4">
            Visualisez
            <br />
            <em className="text-[#FF5A00] not-italic font-normal">votre nouvelle façade</em>
          </h2>
          <p className="text-neutral-400 text-base md:text-lg">
            Uploadez une photo → l&apos;IA génère 5 propositions → personnalisez et régénérez.
          </p>
        </motion.div>

        {/* Input photo partagé par toutes les étapes */}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={onFileChange}
          data-testid="configurator-file-input"
        />

        {/* Étape 1 : upload */}
        {!photo && <UploadArea onDrop={onDrop} onOpenPicker={openPicker} />}

        {/* Étape 2 : photo uploadée, avant génération IA */}
        {photo && aiVariants.length === 0 && (
          <PhotoIntro
            photo={photo}
            aiLoading={aiLoading}
            onGenerate={generateAI}
            onOpenPicker={openPicker}
          />
        )}

        {/* Étapes 3 & 4 : propositions générées — aperçu, variantes, options */}
        {photo && aiVariants.length > 0 && (
          <div className="space-y-8">
            <ResultPreview
              displayedImage={displayedImage}
              applyLoading={applyLoading}
              generated={generated}
              selectedVariant={selectedVariant}
              onOpenPicker={openPicker}
            />

            <VariantsGrid
              photo={photo}
              variants={aiVariants}
              selectedVariant={selectedVariant}
              onSelect={selectVariant}
            />

            {/* Panneau de demande de devis rapide — CTA + mini-form + succès */}
            {(selectedVariant || generated) && !applyLoading && (
              <QuickLeadPanel
                testIdPrefix="configurator-quick-quote"
                ctaLabel="Envoyer une demande de devis"
                ctaTitle="Votre façade vous plaît ?"
                ctaSubtitle="Envoyez-nous votre configuration en un clic — GMC vous rappelle rapidement pour votre devis gratuit."
                formHelper="Votre photo, la façade générée par l'IA, le style choisi et les options sélectionnées seront joints automatiquement à votre demande — vous n'avez rien d'autre à faire."
                buildPayload={buildQuickLeadPayload}
                secondaryAction={
                  <button
                    type="button"
                    onClick={newModification}
                    data-testid="configurator-new-modification"
                    className="btn-ghost justify-center"
                  >
                    <RefreshCw size={16} /> Faire une nouvelle modification
                  </button>
                }
              />
            )}

            <OptionsPanel
              enduit={enduit}
              onEnduit={setEnduit}
              deco={deco}
              onToggleDeco={(item) => toggle(deco, setDeco, item)}
              light={light}
              onToggleLight={(item) => toggle(light, setLight, item)}
              totalSelected={totalSelected}
              hasAnyOption={hasAnyOption}
              applyLoading={applyLoading}
              onRegenerate={regenerate}
            />
          </div>
        )}
      </div>
    </section>
  );
}
